import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtNok } from "@/lib/format";

const WD = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
const NEW = "__new";
const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Oslo" });

const daysOf = (p) => {
  const out = [];
  const d = new Date(`${p.start}T12:00:00Z`);
  const end = new Date(`${p.end}T12:00:00Z`);
  while (d <= end) {
    out.push({ date: d.toISOString().slice(0, 10), wd: WD[(d.getUTCDay() + 6) % 7], dm: `${d.getUTCDate()}.${d.getUTCMonth() + 1}.` });
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
};

const L = ({ children }) => <label className="block text-xs font-medium text-slate-600 mb-1">{children}</label>;

const PeriodPicker = ({ cid, periods, value, onChange }) => {
  const [week, setWeek] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [busy, setBusy] = useState(false);
  const manual = value === "manual";
  const find = async () => {
    const w = parseInt(week, 10), y = parseInt(year, 10);
    if (!(w >= 1 && w <= 53)) return toast.error("Skriv inn et ukenummer mellom 1 og 53");
    if (!(y >= 2000 && y <= 2100)) return toast.error("Skriv inn et gyldig år");
    setBusy(true);
    try {
      const { data } = await api.get(`/customers/${cid}/pax/period`, { params: { year: y, week: w } });
      onChange(data.start, data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Select value={value} onValueChange={(v) => onChange(v)}>
        <SelectTrigger data-testid="pax-manual-period"><SelectValue placeholder="Velg periode" /></SelectTrigger>
        <SelectContent>
          {periods.map((p) => <SelectItem key={p.start} value={p.start}>{p.label}</SelectItem>)}
          <SelectItem value="manual">Annen uke – skriv inn ukenummer</SelectItem>
        </SelectContent>
      </Select>
      {manual && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-28"><L>Ukenummer</L><Input inputMode="numeric" value={week} onChange={(e) => setWeek(e.target.value)} placeholder="f.eks. 27" data-testid="pax-manual-week" /></div>
          <div className="w-28"><L>År</L><Input inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value)} data-testid="pax-manual-year" /></div>
          <Button type="button" variant="outline" onClick={find} disabled={busy || !week} data-testid="pax-manual-week-go">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Velg uke</Button>
        </div>
      )}
    </div>
  );
};

const initial = (actorId) => ({ actor: actorId || "", name: "", contact: "", email: "", phone: "", period: "", counts: {}, received: today(), late: null });

export const ManualReportDialog = ({ cid, open, onClose, onSaved, actors, periods, price, lateFee }) => {
  const [f, setF] = useState(initial());
  const [extra, setExtra] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(initial(open.actorId)); setExtra([]); } }, [open]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const all = useMemo(() => [...periods, ...extra.filter((e) => !periods.some((p) => p.start === e.start))], [periods, extra]);
  const period = all.find((p) => p.start === f.period);
  const days = period ? daysOf(period) : [];
  const total = days.reduce((s, d) => s + (parseInt(f.counts[d.date], 10) || 0), 0);
  const autoLate = period ? f.received > period.deadline.slice(0, 10) : false;
  const late = f.late === null ? autoLate : f.late;
  const pickPeriod = (v, p) => { if (p) setExtra((x) => [...x, p]); set({ period: v, counts: {} }); };

  const submit = async () => {
    if (!f.actor) return toast.error("Velg rederi");
    if (!period) return toast.error("Velg periode");
    const counts = days.map((d) => f.counts[d.date]);
    if (counts.some((v) => !/^\d+$/.test(String(v ?? "").trim()))) return toast.error("Fyll inn antall for alle dager. Skriv 0 for dager uten passasjerer.");
    setBusy(true);
    try {
      await api.post(`/customers/${cid}/pax/manual`, {
        actor_id: f.actor === NEW ? null : f.actor, rederi: f.name, contact: f.contact, email: f.email.trim() || null, phone: f.phone,
        period_start: period.start, counts: counts.map((v) => parseInt(v, 10)), received_date: f.received, late: f.late,
      });
      toast.success("Rapporten er registrert");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pax-manual-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Registrer rapport manuelt</DialogTitle>
          <DialogDescription>For rapporter dere har fått på e-post, papir eller telefon. Rapporten havner i oversikten og månedsrapporten som en vanlig innsending.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <L>Rederi *</L>
              <Select value={f.actor} onValueChange={(v) => set({ actor: v })}>
                <SelectTrigger data-testid="pax-manual-actor"><SelectValue placeholder="Velg rederi" /></SelectTrigger>
                <SelectContent>
                  {actors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  <SelectItem value={NEW}>+ Nytt rederi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.actor === NEW && (
              <>
                <div className="sm:col-span-2"><L>Navn på rederiet *</L><Input value={f.name} onChange={(e) => set({ name: e.target.value })} data-testid="pax-manual-name" /></div>
                <div><L>Kontaktperson</L><Input value={f.contact} onChange={(e) => set({ contact: e.target.value })} data-testid="pax-manual-contact" /></div>
                <div><L>Telefon</L><Input value={f.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="pax-manual-phone" /></div>
                <div className="sm:col-span-2"><L>E-post</L><Input type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} data-testid="pax-manual-email" /></div>
              </>
            )}
            <div className="sm:col-span-2"><L>Gjelder periode *</L><PeriodPicker cid={cid} periods={all} value={period ? f.period : f.period === "manual" ? "manual" : ""} onChange={pickPeriod} /></div>
          </div>
          {period && (
            <>
              <div>
                <L>Antall passasjerer per dag *</L>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {days.map((d) => (
                    <div key={d.date} className="text-center">
                      <div className="text-xs font-semibold text-slate-700">{d.wd}</div>
                      <div className="text-[11px] text-slate-500 mb-1">{d.dm}</div>
                      <Input inputMode="numeric" className="text-center font-semibold px-1 placeholder:text-slate-300" placeholder="–" value={f.counts[d.date] ?? ""}
                        onChange={(e) => set({ counts: { ...f.counts, [d.date]: e.target.value.replace(/\D/g, "") } })} data-testid={`pax-manual-day-${d.date}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 items-end">
                <div><L>Mottatt dato *</L><Input type="date" value={f.received} max={today()} onChange={(e) => set({ received: e.target.value, late: null })} data-testid="pax-manual-received" /></div>
                <label className="flex items-center gap-2 text-sm text-slate-700 h-10 cursor-pointer">
                  <Checkbox checked={late} onCheckedChange={(v) => set({ late: !!v })} data-testid="pax-manual-late" />
                  Levert for sent (gebyr kr {fmtNok(lateFee)})
                </label>
              </div>
              <p className="text-xs text-slate-500 -mt-2">Frist var {period.deadline_label}. «For sent» settes automatisk ut fra mottatt dato, men du kan endre det.</p>
              <div className="flex flex-wrap justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm" data-testid="pax-manual-sum">
                <span>Sum passasjerer: <b className="text-sky-700">{total}</b></span>
                <span>Beløp: <b className="text-sky-700">kr {fmtNok(total * price + (late ? lateFee : 0))}</b>{late && <span className="text-slate-500"> (inkl. gebyr)</span>}</span>
              </div>
            </>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="pax-manual-cancel">Avbryt</Button>
          <Button onClick={submit} disabled={busy} data-testid="pax-manual-save">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Registrer rapport</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
